import {
  ApiDashboardAccount,
  useGetByTenantAccountDashboardQuery,
  useGetByTenantAccountQuery
} from '@/api'
import { TENANT } from '@/const'
import { useNotesFiltersContext } from '@/routing/Reporting'

const NON_TREE_FIELDS = [
  'note-filters-search',
  'notes-currency',
  'notes-institutional',
  'notes-retail',
  'collateralYes',
  'collateralNo'
]

export const useNoteFilters = () => {
  const { filters } = useNotesFiltersContext()

  const currencyCode = filters['notes-currency'] === 'all' ? undefined : filters['notes-currency']

  const retailClientType = filters['notes-retail']
  const institutionalClientType = filters['notes-institutional']
  const isAnyClientTypeActive = institutionalClientType !== retailClientType
  let clientType
  if (isAnyClientTypeActive) {
    if (retailClientType) clientType = 'retail'
    if (institutionalClientType) clientType = 'institutional'
  }

  const nonTreeFilters = Object.entries(filters).filter(
    ([filter]) => !NON_TREE_FIELDS.includes(filter)
  )
  const checkedNonTreeFilters = nonTreeFilters.filter(([, value]) => value === true)
  const noteIds =
    checkedNonTreeFilters.length === 0
      ? undefined
      : checkedNonTreeFilters.reduce((string, [noteId]) => `${string},${noteId}`, '')

  const collateral =
    filters.collateralYes !== filters.collateralNo ? filters.collateralYes : undefined

  return {
    currencyCode,
    noteIds,
    clientType,
    collateral
  }
}

export const useFilteredGetByTenantAccountDashboardQuery = () => {
  const { clientType, collateral, currencyCode, noteIds } = useNoteFilters()

  return useGetByTenantAccountDashboardQuery({
    tenant: TENANT,
    currencyCode,
    noteIds,
    clientType,
    isNoteCollateralized: collateral
  })
}

export type FilteredDashboardAccount = ApiDashboardAccount & { isCollateralForLoan?: boolean }

export const useFilteredGetByTenantAccountQuery = () => {
  const { clientType, collateral, currencyCode, noteIds } = useNoteFilters()

  return useGetByTenantAccountQuery(
    {
      tenant: TENANT,
      currencyCode,
      noteIds,
      clientType,
      isNoteCollateralized: collateral
    },
    {
      selectFromResult: ({ data, ...props }) => ({
        ...props,
        data:
          data &&
          data.accounts?.map(account => {
            const clone: FilteredDashboardAccount = { ...account }

            // Add the portfolio name
            clone.portfolioName = data.portfolios?.find(
              portfolio => portfolio.portfolioId === account.portfolioId
            )?.portfolioName

            // Add the note name
            const note = data.notes?.find(note => note.noteId === account.noteId)
            clone.noteName = note?.noteName
            clone.isCollateralForLoan = note.isCollateralForLoan

            return clone
          })
      })
    }
  )
}
